import { Stack } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { Link } from './Link';
import {
  expandedLinkGroupIdState,
  isSideDrawerExpandedState,
  userDashAccessState,
  userIsLoggedInState,
  userSubLevelState,
} from '../../states';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  HOME_LINK,
  INTERNAL_ONLY_PRODUCTS,
  NON_PROD,
  PRODUCT_LINKS,
  TRAINING_LINK,
} from '../../config';
import { hasDashAccessToProduct } from '../../util/user';
import { ProductGroup, ProductType, SubLevel } from '../../types';
import useSetSym from 'hooks/hiro/useSetSym';
import GroupHeader from './sidebar/GroupHeader';
import { isValidTokenOverrideTimeframe } from 'util/user';
import useAuth from 'hooks/auth/useAuth';
import { BetaTag } from '../shared/Tag';

const DEFAULT_PRODUCT_GROUP: Record<ProductGroup, ProductType[]> = {
  [ProductGroup.MARKET_CENTRAL]: [
    ProductType.FOUNDERS_NOTES,
    ProductType.REPORTS,
    ProductType.OPTIONS_CALCULATOR,
    ProductType.INDICES,
    ProductType.CANVAS,
    ProductType.INTEGRATIONS,
  ],
  [ProductGroup.EQUITY_CONSOLE]: [ProductType.EQUITYHUB, ProductType.SCANNERS],
  [ProductGroup.REAL_TIME_SIGNALS]: [
    ProductType.TAPE,
    ProductType.HIRO,
    ProductType.IMPLIED_VOL,
    ProductType.TRACE,
  ],
  [ProductGroup.INTERNAL]: INTERNAL_ONLY_PRODUCTS,
};

const PRODUCT_GROUPS_BY_SUB_LEVEL: Record<
  SubLevel,
  Record<ProductGroup, ProductType[]>
> = {
  [SubLevel.ESSENTIAL]: DEFAULT_PRODUCT_GROUP,
  [SubLevel.PRO]: {
    [ProductGroup.EQUITY_CONSOLE]:
      DEFAULT_PRODUCT_GROUP[ProductGroup.EQUITY_CONSOLE],
    [ProductGroup.MARKET_CENTRAL]:
      DEFAULT_PRODUCT_GROUP[ProductGroup.MARKET_CENTRAL],
    [ProductGroup.REAL_TIME_SIGNALS]: [
      ProductType.TAPE,
      ProductType.IMPLIED_VOL,
      ProductType.HIRO,
      ProductType.TRACE,
    ],
    [ProductGroup.INTERNAL]: DEFAULT_PRODUCT_GROUP[ProductGroup.INTERNAL],
  },
  [SubLevel.ALPHA]: {
    [ProductGroup.REAL_TIME_SIGNALS]: [
      ProductType.HIRO,
      ProductType.IMPLIED_VOL,
      ProductType.TRACE,
      ProductType.TAPE,
    ],
    [ProductGroup.EQUITY_CONSOLE]:
      DEFAULT_PRODUCT_GROUP[ProductGroup.EQUITY_CONSOLE],
    [ProductGroup.MARKET_CENTRAL]:
      DEFAULT_PRODUCT_GROUP[ProductGroup.MARKET_CENTRAL],
    [ProductGroup.INTERNAL]: DEFAULT_PRODUCT_GROUP[ProductGroup.INTERNAL],
  },
  [SubLevel.NONE]: DEFAULT_PRODUCT_GROUP,
};

export const Products = () => {
  const isMobile = useIsMobile();
  const { getSymRaw } = useSetSym();
  const [isDrawerExpanded, setIsDrawerExpanded] = useRecoilState(
    isSideDrawerExpandedState,
  );

  const isLoggedIn = useRecoilValue(userIsLoggedInState);
  const { hasAccessToProduct, productsWithAccess } = useAuth();
  const userLevel = useRecoilValue(userSubLevelState);
  const userDashAccess = useRecoilValue(userDashAccessState);
  const setExpandedLinkGroupId = useSetRecoilState(expandedLinkGroupIdState);

  const closeSidebar = () => {
    setIsDrawerExpanded(false);
    setExpandedLinkGroupId(undefined);
  };

  const onLinkClick = () => {
    if (isMobile) {
      closeSidebar();
    }
  };

  const getMergedLink = (link: string) => {
    let finalUrl = link;
    const symParam = getSymRaw();
    if (symParam != null) {
      const searchParams = new URLSearchParams();
      searchParams.set('sym', symParam);
      finalUrl = `${finalUrl}?${searchParams.toString()}`;
    }

    return finalUrl;
  };

  return (
    <Stack gap={7}>
      <Stack sx={{ gap: 1 }}>
        <Link
          isLabelShown={isDrawerExpanded}
          key={HOME_LINK.label}
          to={getMergedLink(HOME_LINK.link)}
          icon={HOME_LINK.icon}
          text={HOME_LINK.label}
          tagText={null}
          isLocked={!hasAccessToProduct(ProductType.HOME)}
          isSpecialUnlocked={
            !productsWithAccess.has(ProductType.HOME) &&
            isValidTokenOverrideTimeframe()
          }
          onClick={onLinkClick}
        />
        <Link
          isLabelShown={isDrawerExpanded}
          key={TRAINING_LINK.label}
          to={getMergedLink(TRAINING_LINK.link)}
          icon={TRAINING_LINK.icon}
          text={TRAINING_LINK.label}
          tagText={null}
          onClick={onLinkClick}
        />
      </Stack>

      {Object.entries(PRODUCT_GROUPS_BY_SUB_LEVEL[userLevel]).map(
        ([groupName, products]) => {
          // make sure we only show products the user has dash access to
          const filteredProducts = products.filter((product: ProductType) =>
            hasDashAccessToProduct(userDashAccess, product),
          );

          if (filteredProducts.length === 0) {
            return null;
          }

          return (
            <Stack key={groupName} sx={{ gap: 2 }}>
              <GroupHeader title={groupName} />
              <Stack sx={{ gap: 1 }}>
                {filteredProducts
                  .filter(
                    (product: ProductType) =>
                      isLoggedIn || product !== ProductType.TAPE, // Tape is only available to logged in users
                  )
                  .map((product: ProductType) => {
                    const {
                      link,
                      label,
                      icon,
                      tagText,
                      betaOnly,
                      subLevelRequired,
                    } = PRODUCT_LINKS[product];

                    const finalUrl = getMergedLink(link);
                    return (
                      (!(betaOnly ?? false) || NON_PROD) && (
                        <Link
                          isLabelShown={isDrawerExpanded}
                          key={label}
                          to={finalUrl}
                          icon={icon}
                          text={label}
                          tagText={tagText}
                          isLocked={!hasAccessToProduct(product)}
                          subLevelRequired={subLevelRequired}
                          isSpecialUnlocked={
                            !productsWithAccess.has(product) &&
                            isValidTokenOverrideTimeframe() &&
                            product !== ProductType.INTEGRATIONS
                          }
                          onClick={onLinkClick}
                        >
                          {betaOnly === true && <BetaTag />}
                        </Link>
                      )
                    );
                  })}
              </Stack>
            </Stack>
          );
        },
      )}
    </Stack>
  );
};
